import { useEffect } from "react";

const SITE = "https://spherehyx.com";
const DEFAULT_TITLE = "SphereHyx — Connect · Manage · Grow";
const DEFAULT_DESC =
  "SphereHyx — all-in-one sports club management: teams, athletes, attendance, payments, subscriptions and inter-club tournaments.";
const DEFAULT_IMAGE = `${SITE}/og-image.png`;

function upsertMeta(kind, key, value) {
  if (value == null || value === "") return;
  let el = document.head.querySelector(`meta[${kind}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(kind, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Dependency-free SEO manager for a React SPA. Upserts (never duplicates) the
// document title, description, keywords, canonical, robots, Open Graph and
// Twitter Card tags. Restores sensible defaults on unmount.
export default function useSeo({ title, description, keywords, canonical, image, type = "website", noindex = false } = {}) {
  useEffect(() => {
    const t = title || DEFAULT_TITLE;
    const d = description || DEFAULT_DESC;
    const url = canonical || (typeof window !== "undefined" ? window.location.href.split(/[?#]/)[0] : SITE);
    const img = image || DEFAULT_IMAGE;

    document.title = t;
    upsertMeta("name", "description", d);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    upsertLink("canonical", url);

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", t);
    upsertMeta("property", "og:description", d);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", t);
    upsertMeta("name", "twitter:description", d);
    upsertMeta("name", "twitter:image", img);

    return () => {
      document.title = DEFAULT_TITLE;
      upsertMeta("name", "description", DEFAULT_DESC);
      upsertMeta("name", "robots", "index, follow");
    };
  }, [title, description, keywords, canonical, image, type, noindex]);
}
